import type { CSSProperties, ReactNode } from "react";
import { textColorForBg, borderColorForBg, tintedBadgeColors } from "@/lib/constants";

interface ColorBadgeProps {
  label: string;
  color: string;
  /** Dropdown option ID — used to look up text color overrides. */
  id?: string;
  /** Use a subtle tinted background (20% opacity) with the full color as text. Used for ticket type badges. */
  tinted?: boolean;
  /**
   * `tinted`'s sibling with the fill taken out: the same ink and the same
   * hairline, over nothing. It is what a HELPER team chip wears, so that the
   * primary team's tinted chip is the only filled thing in the row and reads
   * as the answer to "whose is this" without being a second solid pill beside
   * the status column.
   */
  outline?: boolean;
  /**
   * Geometry. "pill" is the original: fully rounded, roomy, `text-xs`.
   * "chip" is the ticket-type chip's shape — a small radius, tighter padding,
   * `text-[11px]` — and is what `tinted` has always rendered as.
   *
   * Solid badges opt into "chip" where they sit beside a type chip and a
   * fully-rounded capsule of the same colour reads as a different species of
   * label (team chips do; see TeamChip). Status and priority badges keep the
   * pill, which is the shape those columns are read in.
   */
  shape?: "pill" | "chip" | "mini";
  /** A glyph before the label — the primary team chip's star. */
  leading?: ReactNode;
}

/** The ticket-type chip's geometry — the shared shape both `tinted` and `shape="chip"` render. */
const CHIP_GEOMETRY = "inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap shrink-0";
/** A step in a pipeline summary (#9304): the chip, a size down, so a long pipeline stays compact. */
const MINI_GEOMETRY =
  "inline-flex items-center px-1.5 py-px rounded text-[10px] leading-4 font-medium whitespace-nowrap shrink-0";

const ColorBadge = ({
  label,
  color,
  id,
  tinted = false,
  outline = false,
  shape = "pill",
  leading,
}: ColorBadgeProps) => {
  if (tinted || outline) {
    const { light, dark } = tintedBadgeColors(color);
    return (
      <span
        className={`colorbadge-tinted ${CHIP_GEOMETRY} border`}
        style={
          {
            // The outline variant is the tinted one with both fills replaced.
            // Same two-properties-and-a-class trick as everywhere else these
            // stored hexes are drawn: an inline style cannot answer `.dark`,
            // so both themes ship and CSS picks.
            "--tinted-bg": outline ? "transparent" : light.background,
            "--tinted-text": light.text,
            "--tinted-border": light.border,
            "--tinted-bg-dark": outline ? "transparent" : dark.background,
            "--tinted-text-dark": dark.text,
            "--tinted-border-dark": dark.border,
          } as CSSProperties
        }
      >
        {leading}
        {label}
      </span>
    );
  }

  return (
    <span
      className={
        shape === "chip"
          ? `${CHIP_GEOMETRY} border`
          : shape === "mini"
            ? `${MINI_GEOMETRY} border`
            : "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap border"
      }
      style={{
        backgroundColor: color,
        color: textColorForBg(color, id),
        borderColor: borderColorForBg(color),
      }}
    >
      {leading}
      {label}
    </span>
  );
};

export default ColorBadge;
